import { z } from "zod";

// ---------------------------------------------------------------------------
// 会社の切替（#151、docs/60。2026-10-07〜08 射場さんの決定 ★1〜★3）
//   ・切替は「会社の切替」、ログインの続きは「会社を選択」。iOS の Inset Grouped の形（docs/10 §9.15）
//   ・並びの既定は「よく使う順」（この30日に使った日数）。「編集」で自分で並べることもできる（人＝accounts ごと）
//   ・会社のアイコン＝自社情報のロゴ、無ければ頭文字（companyInitial）
//   ・ほかの製品の会社は、押すと相手のドメインへ移ってそのままその会社に入る（1回きりの鍵 handoff、2分・同じ端末だけ）
// このファイルは zod だけを読む（auth.ts・users.ts から読まれるので、ここから auth.ts を読まない＝循環しない）
// ---------------------------------------------------------------------------

/** 並び順：auto＝よく使う順（既定）／manual＝自分で並べる（accounts.company_order） */
export const CompanyOrder = z.enum(["auto", "manual"]);
export type CompanyOrder = z.infer<typeof CompanyOrder>;

export const SWITCHER_MSG = {
  /** 題：ログイン中の切替（アカウントのメニューの項目も同じ） */
  title: "会社の切替",
  /** 題：ログインの続き */
  chooseTitle: "会社を選択",
  current: "使用中",
  last: "前回",
  member: "担当者",
  /** 会社名がまだ無い会社（会員登録の直後） */
  noName: "会社名は未登録",
  /** 一覧の下の行（#185：「会社を追加」から改名 → #186「新しい会社の作成」。PC だけ） */
  add: "新しい会社の作成",
  logout: "ログアウト",
  loggedInAs: (email: string) => `${email} でログイン中`,
  /** ほかの製品の節の注（押すと相手のドメインへ移る） */
  goesTo: (productName: string) => `${productName}のサイトへ切替`,
  // 並び替え（「編集」の中）
  edit: "編集",
  reorder: "並び替え",
  /** 並び替えの帯の左右（ボタンの共通の文言、docs/10 §5.1.2） */
  cancel: "取消",
  done: "決定",
  orderHead: "並び順",
  auto: "よく使う順",
  manual: "自分で並べる",
  autoNote: "よく使う順＝この30日に使った日が多い会社が上",
  grip: (name: string) => `${name}の並び替え`,
  moveTop: "一番上へ",
  moveUp: "上へ",
  moveDown: "下へ",
  moveBottom: "一番下へ",
  /** 使えない会社の下の字（朱） */
  blocked: { disabled: "管理者が使えない設定にしています", closed: "退会の手続き中", suspended: "利用停止中" },
  // 製品をまたぐ切替（handoff）
  moving: "移動しています",
  switched: (name: string) => `${name} に切り替えました`,
  /** 鍵が使えない・鍵が無い（API の 400 AUTH_HANDOFF_INVALID の title も同じ。#192 で「会社の切替から」を足した） */
  handoffFailed: "切り替えられませんでした。会社の切替からもう一度お試しください",
  // 確認の画面（/handoff、#194。2026-10-16 射場さん：メールアドレスでなく氏名で。Figma「会社の切替の確認（10/16）」①〜④）
  /** PC の題の下の一文（API の 428 HANDOFF_CONFIRM の title も同じ） */
  handoffLead: "次の会社に切り替えます。",
  handoffPerson: "利用者",
  handoffLoginId: "ログインID",
  handoffGo: "切替",
  handoffNothing: "心当たりがない場合は、この画面を閉じてください。",
  /** 別の人がログイン中の注意の帯（409。メールが分からなければ「別のアカウントで」） */
  handoffOtherBand: (currentEmail: string | null) => `現在、別のアカウント${currentEmail ? `（${currentEmail}）` : ""}でログイン中です。切り替えると、そのログインは終了します。`,
  handoffOtherNote: "現在のログインのまま使う場合は、この画面を閉じてください。",
  /** API の 409 HANDOFF_OTHER_ACCOUNT の title（画面は handoffOtherBand） */
  handoffOther: "このブラウザは別のアカウントでログインしています",
  handoffLogoutEnter: "ログアウトして切替",
  /** 切り替えられなかったときのボタン：このドメインでログインしていなければ「ログイン」、していれば「会社切替へ」（/choose?switch。#192） */
  handoffLogin: "ログイン",
  handoffToSwitcher: "会社切替へ",
  /** POST /auth/handoff/start・/auth/choose/handoff に同じ製品の会社を送った（400。同じ製品は /auth/switch・/auth/choose） */
  handoffSameProduct: "同じ製品の会社は、この画面で切り替えます",
  /** 「自分で並べる」で会社の id が足りない（400） */
  orderRequired: "並びを送ってください",
} as const;

/** 前後から外す法人の種類（長いものから）。「株式会社ミヤビ」→「ミ」 */
const CORP_WORDS = [
  "特定非営利活動法人",
  "一般社団法人",
  "一般財団法人",
  "公益社団法人",
  "公益財団法人",
  "株式会社",
  "有限会社",
  "合同会社",
  "合資会社",
  "合名会社",
  "NPO法人",
  "（株）",
  "(株)",
  "㈱",
  "（有）",
  "(有)",
  "㈲",
] as const;

/**
 * 会社の頭文字（アイコンの字）。法人の種類を前後から外し、空白を詰めた1文字目（英字は大文字）。空なら null（画面は建物のアイコン）
 *   「株式会社ミヤビクリエイティブ」→「ミ」、「アイバックス株式会社」→「ア」、「(株) test」→「T」
 */
export function companyInitial(name: string | null | undefined): string | null {
  let s = (name ?? "").replace(/[\s　]+/g, "");
  for (let changed = true; changed; ) {
    changed = false;
    for (const w of CORP_WORDS) {
      if (s.startsWith(w)) {
        s = s.slice(w.length);
        changed = true;
      }
      if (s.endsWith(w) && s.length > w.length) {
        s = s.slice(0, -w.length);
        changed = true;
      }
    }
  }
  const first = Array.from(s)[0];
  return first ? first.toUpperCase() : null;
}
